import List from '@/components/Elements/List/List';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/elements/dialog';
import { Input } from '@/components/ui/elements/input';
import { AuthContext } from '@/providers/auth';
import { LocationContext } from '@/providers/location';
import { BuildingOfficeIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useContext, useState } from 'react';

type LocationSelectorProps = {
  open: boolean;
  selectedValue: number | undefined;
  listOptions: any[];
  setOpen: (value: boolean) => void;
  onClose: (value: number | undefined) => void;
  areaop: string;
};

const LocationSelector = ({
  onClose,
  selectedValue,
  open,
  setOpen,
  listOptions,
  areaop,
}: LocationSelectorProps) => {
  const [filter, setFilter] = useState<string | null>(null);

  const handleListItemClick = (value: number) => {
    onClose(value);
    setFilter(null);
  };

  const onFilterChange = (value: string) => {
    setFilter(value.toLowerCase());
  };

  const handleOpenChange = (value: boolean) => {
    setFilter(null);
    setOpen(value);
  };

  listOptions = filter
    ? listOptions.filter(
        (option) =>
          option.areaop.toLowerCase().includes(filter) ||
          option.abbr?.toLowerCase().includes(filter),
      )
    : listOptions;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="h-125">
          <DialogHeader>
            <DialogTitle>{areaop}</DialogTitle>
          </DialogHeader>
          <Input placeholder="Search..." onChange={(event) => onFilterChange(event.target.value)} />
          <div className="border-t h-80 overflow-y-scroll">
            <List className="">
              {listOptions?.map((access) => (
                <List.ItemButton
                  key={access.franchise_id}
                  onClick={() => handleListItemClick(access.franchise_id)}
                  className="w-full"
                >
                  {selectedValue === access.franchise_id ? (
                    <CheckIcon className="h-5 w-5 mr-2" />
                  ) : (
                    <span className="h-5 w-5 mr-2" />
                  )}
                  {access.areaop}
                </List.ItemButton>
              ))}
            </List>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const FranchisePicker = () => {
  const [open, setOpen] = useState(false);
  const { location_access } = useContext(AuthContext);
  const { activeLocation, activeLocationId, selectLocation } = useContext(LocationContext);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = (value: number | undefined) => {
    setOpen(false);
    if (value !== undefined && value > -1) {
      selectLocation(value);
    }
  };

  if (!location_access) {
    return null;
  }

  if (location_access.length === 0 && activeLocationId !== undefined) {
    return null;
  }

  return (
    <div className="ml-4">
      <button
        className="text-foreground/90 py-0.5 pl-0.5 border rounded-full cursor-pointer bg-muted/25 hover:bg-foreground/5 hover:shadow-xs"
        type="button"
        onClick={handleClickOpen}
      >
        <div className="flex flex-row px-2 py-0.5">
          <BuildingOfficeIcon className="h-6 md:w-6 lg:h-7 lg:w-7 text-foreground/70" />
          <div className="hidden md:block text-left max-w-56">
            <span className="text-xs font-semibold tracking-tighter">
              {activeLocation?.areaop || 'Pick a Location'}
            </span>
          </div>
        </div>
      </button>
      {location_access && (
        <LocationSelector
          selectedValue={activeLocationId}
          open={open}
          setOpen={setOpen}
          onClose={handleClose}
          listOptions={location_access}
          areaop={activeLocation?.areaop || 'Pick a Location'}
        />
      )}
    </div>
  );
};
